import React, { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { colors } from "@/lib/theme";
import { Spinner } from "@/components/peeps/ui";
import { pushSupported, isPushEnabled, enablePush, disablePush } from "@/lib/push";

export function PushToggle() {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    isPushEnabled().then(setEnabled).catch(() => setEnabled(false));
  }, []);

  const explainUnsupported = () => {
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (isIOS && !standalone) {
      toast("On iPhone, add peeps to your Home Screen first (Share → Add to Home Screen), then open it from there to turn on notifications.");
    } else {
      toast("Notifications aren't supported in this browser. Try Chrome, or add the app to your Home Screen.");
    }
  };

  const toggle = async (next) => {
    setBusy(true);
    try {
      if (next) {
        await enablePush();
        setEnabled(true);
        toast("Push notifications are on — you'll get pinged on this device");
      } else {
        await disablePush();
        setEnabled(false);
        toast("Push notifications turned off on this device");
      }
    } catch (e) {
      toast(e.message || "Couldn't update notifications");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="push-toggle-row" className="flex items-center gap-3 rounded-xl p-4" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
      {enabled ? <Bell size={18} color={colors.brandSecondary} /> : <BellOff size={18} color={colors.muted} />}
      <div className="flex-1 min-w-0">
        <p className="font-display font-medium text-[15px]" style={{ color: colors.onSurface }}>Push notifications</p>
        <p className="font-display text-[12px]" style={{ color: colors.faint }}>
          {supported ? "Get pinged on this device for plans, replies & nudges" : "Not supported on this browser"}
        </p>
      </div>
      {busy ? (
        <Spinner />
      ) : (
        <button
          data-testid="push-toggle-switch"
          onClick={() => (supported ? toggle(!enabled) : explainUnsupported())}
          className="relative inline-flex items-center rounded-full transition-colors shrink-0"
          style={{ width: 42, height: 24, background: enabled ? colors.brand : colors.surfaceTertiary, opacity: supported ? 1 : 0.5 }}
        >
          <span className="absolute rounded-full bg-white transition-transform" style={{ width: 18, height: 18, top: 3, left: 3, transform: enabled ? "translateX(18px)" : "translateX(0)" }} />
        </button>
      )}
    </div>
  );
}
